// DispatchDeck — application audit, LIVE checks (TASK-9444 slice 2)
// ============================================================================
// `window.ApplicationAuditLive` — the "Things to Verify" checks that depend on
// events AFTER the application was submitted, so they cannot be a submit-time
// snapshot (slice 1's `applicationAudit`, computed by saveDriverApplication).
// They are evaluated at RENDER time from data the HR viewer already reads:
//
//   2. checkr_consider_unadjudicated — the Checkr result is consider / review
//      and no decision (Clear / Hold / Adverse Action) has been recorded on the
//      peopleBackgroundChecks record, while the applicant is still in the
//      pipeline. Points at the FCRA adverse-action path.
//   3. flag_open_after_proof — a compliance-partner applicant flag is still
//      active, and a report document for the same item was attached (or a
//      Checkr report completed) AFTER the flag was set.
//
// INPUTS (all already readable by the hiring screen — no new reads, no rules
// change): the candidate doc (`checkrReport*`, `backgroundReports`, `status`),
// the bg record from the peopleBackgroundChecks listener
// (HRState.bgRecordsByCandidateId), and the active flags from the
// candidateFlags listener (window.PersonSections.activeCandidateFlags).
//
// 🔴 GUARDRAILS (same as slice 1, TASK-9444 row):
//   - Facts to verify. No score, severity or hire/no-hire recommendation.
//     Nothing reads these items to block, reject, or change a stage. FCRA
//     adverse action stays a human decision.
//   - Deterministic, no AI, no network, no writes.
//   - Messages never echo the applicant's email, phone, SSN, DOB or licence
//     number. A partner's flag reason (shown to the same viewers in the flag
//     panel already) is quoted and clipped; every renderer MUST escape.
//   - Missing input = "did not run", never "all clear": when the bg listener
//     has not loaded, check 2 says nothing rather than claim "not decided".
//
// LOADING (equipmentNotes.js / personSections.js precedent, Pitfall #1):
// side-effect module, imported by views/hr.js. Readers use
// `window.ApplicationAuditLive` at call time, never an ES import of a symbol.
// Self-contained (no imports) so tests load it in a vm sandbox.
// ============================================================================
(function () {
  'use strict';

  const VERSION = 1;

  const str = (v) => (v == null ? '' : String(v)).trim();
  const lc = (v) => str(v).toLowerCase();
  const clip = (s, n) => (s.length > n ? s.slice(0, n - 1) + '…' : s);
  const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

  // Timestamp | {seconds} | {_seconds} | Date | ms | ISO → ms, else null.
  function toMillis(v) {
    if (v == null || v === '') return null;
    try {
      if (typeof v.toMillis === 'function') return v.toMillis();
      if (typeof v.toDate === 'function') return v.toDate().getTime();
      if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
      if (typeof v === 'number') return isFinite(v) ? v : null;
      if (typeof v.seconds === 'number') return v.seconds * 1000;
      if (typeof v._seconds === 'number') return v._seconds * 1000;
      if (typeof v === 'string') {
        // A bare day ("2026-09-28") has no time — refuse it rather than guess
        // a time of day that could land before or after a flag set that day.
        if (/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return null;
        const ms = Date.parse(v);
        return isNaN(ms) ? null : ms;
      }
    } catch (_) { /* fall through */ }
    return null;
  }

  function fmtDay(ms, fmt) {
    if (ms == null) return '';
    if (typeof fmt === 'function') { try { const s = fmt(ms); if (s) return String(s); } catch (_) { /* default */ } }
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ── CHECK 2 — Checkr consider / review with no recorded decision ──────────

  // A human decision recorded on peopleBackgroundChecks (setBgDecision /
  // the adverse-action flow). 'awaiting_review' is the system's "nobody has
  // decided yet" marker, so it does NOT count.
  const DECIDED = ['cleared', 'held', 'adverse_action_initiated', 'aa_pre_notice_sent'];
  // Pipeline statuses where the hiring decision is already made (or the
  // applicant left). Spec: flag while the applicant is still in the pipeline.
  const CLOSED_STATUSES = ['hired', 'rejected', 'withdrawn', 'draft'];
  const CONSIDER_RESULTS = ['consider'];
  const REVIEW_ASSESSMENTS = ['review', 'escalated'];

  function _needsReview(result, status, assessment) {
    return CONSIDER_RESULTS.indexOf(lc(result)) >= 0
      || CONSIDER_RESULTS.indexOf(lc(status)) >= 0
      || REVIEW_ASSESSMENTS.indexOf(lc(status)) >= 0
      || REVIEW_ASSESSMENTS.indexOf(lc(assessment)) >= 0;
  }

  /**
   * @param c        candidate doc
   * @param bg       peopleBackgroundChecks/{candidateId} projection, or null
   * @param opts     { bgKnown: true when the bg listener has loaded, so a
   *                   missing `bg` really means "no record" }
   * @returns {code,message} | null
   */
  function checkCheckrUnadjudicated(c, bg, opts) {
    if (!isObj(c)) return null;
    const o = opts || {};
    if (CLOSED_STATUSES.indexOf(lc(c.status)) >= 0) return null;
    if (c.archived === true) return null;
    const rec = isObj(bg) ? bg : null;
    // The bg record is the canonical owner once it has a report; the candidate
    // fields are the Checkr webhook's mirror and cover the embed flow, which
    // never writes a bg record.
    const fromBg = !!(rec && (rec.latestReportId || rec.latestStatus || rec.latestAssessment));
    const needs = fromBg
      ? _needsReview(null, rec.latestStatus, rec.latestAssessment)
      : _needsReview(c.checkrReportResult, c.checkrReportStatus, c.checkrReportAssessment);
    if (!needs) return null;
    if (rec) {
      // The CURRENT decision wins (COD r1): a record moved back to
      // 'awaiting_review' (e.g. a renewed report) is undecided again even if
      // an older entry in the history was a decision. Only when the current
      // field is empty does the newest history entry stand in for it.
      let current = str(rec.bgDecision);
      if (!current) {
        const hist = (Array.isArray(rec.bgDecisionHistory) ? rec.bgDecisionHistory : []).filter(isObj);
        let newest = null; let newestMs = -Infinity;
        hist.forEach((h, i) => {
          const ms = toMillis(h.at || h.decidedAt || h.timestamp);
          const key = ms == null ? i - 1e15 : ms; // untimed rows keep array order, behind timed ones
          if (key >= newestMs) { newestMs = key; newest = h; }
        });
        current = newest ? str(newest.decision) : '';
      }
      if (DECIDED.indexOf(current) >= 0) return null;
    } else if (o.bgKnown !== true) {
      // Decision records not loaded (listener error / not yet subscribed):
      // we cannot tell whether someone decided. Say nothing.
      return null;
    }
    return {
      code: 'checkr_consider_unadjudicated',
      message: 'Background check came back “consider” (needs review) and no decision is recorded yet. Open the report and record Clear, Hold or Adverse Action. If the report is your reason to decline, use Reject… so the FCRA adverse-action notices go out.',
    };
  }

  // ── CHECK 3 — partner flag still active after later proof ─────────────────

  const REPORT_LABELS = { clearinghouse: 'Clearinghouse', mvr: 'MVR', cdlis: 'CDLIS', psp: 'PSP' };

  // Which report a flag is about. Typed flags whose remedy is NOT a report
  // (missing CDL / med card uploads carry no server timestamp on a candidate;
  // employer-history fixes are edits, not documents; a later MVR does not cure
  // an undisclosed violation) map to nothing. Free-text flags are matched on
  // their reason.
  const REASON_TO_REPORT = [
    // Narrow on purpose (COD r1): "drug" / "alcohol" / "prohibited" alone also
    // describe test results and other issues, not a pending Clearinghouse query.
    { key: 'clearinghouse', re: /clearing\s*house|not\s+(?:yet\s+)?legal\s+to\s+drive|full\s+query/i },
    { key: 'cdlis', re: /\bcdlis\b/i },
    { key: 'psp', re: /\bpsp\b/i },
    { key: 'mvr', re: /\bmvr\b|motor\s+vehicle\s+record|driving\s+record/i },
  ];
  const TYPED_FLAG_REPORTS = {
    mvr_undisclosed: [],
    missing_cdl: [],
    missing_medcard: [],
    missing_employer_history: [],
    vague_employer_history: [],
  };

  function reportKeysForFlag(flag) {
    if (!isObj(flag)) return [];
    const type = str(flag.flagType) || 'other';
    if (Object.prototype.hasOwnProperty.call(TYPED_FLAG_REPORTS, type)) return TYPED_FLAG_REPORTS[type].slice();
    const reason = str(flag.reason);
    if (!reason) return [];
    return REASON_TO_REPORT.filter((r) => r.re.test(reason)).map((r) => r.key);
  }

  // Server-stamped proof events on the candidate's report rows. Day-only
  // `date` stamps are ignored (no time → cannot order against a flag set the
  // same day). Applicant-uploaded `documents.*` carry no timestamp and are
  // applicant-controlled, so they are never proof here.
  function proofEvents(c, key) {
    const reports = isObj(c && c.backgroundReports) ? c.backgroundReports : {};
    const row = isObj(reports[key]) ? reports[key] : null;
    if (!row) return [];
    const out = [];
    const up = toMillis(row.docUploadedAt);
    if (up != null && (row.docPath || row.docUrl)) out.push({ key, ms: up, kind: 'document' });
    if (key === 'mvr' && lc(row.updatedBy) === 'checkr-webhook') {
      const done = toMillis(row.completedAt);
      if (done != null) out.push({ key, ms: done, kind: 'report' });
    }
    return out;
  }

  /**
   * @param c      candidate doc
   * @param flags  ACTIVE applicant flags for this candidate (the
   *               PersonSections.activeCandidateFlags output: {flagType,
   *               flagged:true, reason, setAt, …})
   * @param opts   { labelFor(flagType) → string, fmtDate(ms) → string }
   * @returns [{code,message}]
   */
  function checkFlagsOpenAfterProof(c, flags, opts) {
    const o = opts || {};
    const list = Array.isArray(flags) ? flags : [];
    const items = [];
    list.forEach((f) => {
      if (!isObj(f) || f.flagged !== true) return;
      const setMs = toMillis(f.setAt);
      if (setMs == null) return; // pending write / no time → cannot order
      let latest = null;
      reportKeysForFlag(f).forEach((key) => {
        proofEvents(c, key).forEach((ev) => {
          if (ev.ms > setMs && (!latest || ev.ms > latest.ms)) latest = ev;
        });
      });
      if (!latest) return;
      const typeLabel = typeof o.labelFor === 'function' ? str(o.labelFor(f.flagType)) : '';
      const reason = clip(str(f.reason).replace(/\s+/g, ' '), 100);
      const what = reason ? (typeLabel && typeLabel !== 'Other issue' ? typeLabel + ': ' + reason : reason) : (typeLabel || 'Flag');
      const proof = REPORT_LABELS[latest.key] + (latest.kind === 'report' ? ' report came back' : ' document was attached');
      items.push({
        code: 'flag_open_after_proof',
        // Neutral (COD r1 Domain): a later document may itself show a problem,
        // so the item asks for a review, never implies the flag is resolved.
        message: 'Compliance partner flag “' + what + '” (set ' + fmtDay(setMs, o.fmtDate) + ') is still open, and a '
          + proof + ' after it was set (' + fmtDay(latest.ms, o.fmtDate) + '). Review what came in and confirm with your compliance partner whether the flag still applies.',
      });
    });
    return items;
  }

  /** Both live checks. ctx = { bgRecord, bgKnown, activeFlags, labelFor, fmtDate } */
  function computeLiveItems(c, ctx) {
    const x = ctx || {};
    const items = [];
    try {
      const two = checkCheckrUnadjudicated(c, x.bgRecord || null, { bgKnown: x.bgKnown === true });
      if (two) items.push(two);
    } catch (_) { /* advisory: a bad row never breaks the card */ }
    try {
      checkFlagsOpenAfterProof(c, x.activeFlags || [], { labelFor: x.labelFor, fmtDate: x.fmtDate })
        .forEach((i) => items.push(i));
    } catch (_) { /* same */ }
    return items;
  }

  const api = {
    VERSION,
    toMillis,
    reportKeysForFlag,
    proofEvents,
    checkCheckrUnadjudicated,
    checkFlagsOpenAfterProof,
    computeLiveItems,
  };
  // window only — no `module.exports`, so esbuild bundles this as a plain
  // side-effect script rather than wrapping it as CommonJS.
  if (typeof window !== 'undefined') window.ApplicationAuditLive = api;
})();
